/**
 * SettingsPanel — Script-level configuration tab.
 * Matches the mockup's Settings tab: execution mode, script type, app key,
 * chaining, schedule, and (collapsed-by-default) Fire Policy.
 *
 * Fire Policy contract (mirrors backend ExtensionScript.firePolicy JSONB):
 *   {
 *     runAt?: 'document_start' | 'document_end' | 'document_idle',
 *     allFrames?: boolean,
 *     excludePatterns?: string[],
 *     oncePerTab?: boolean,
 *     refireOnSpaNav?: boolean,
 *     world?: 'main' | 'isolated',
 *   }
 *
 * If the user clears every field back to defaults the parent receives
 * `firePolicy: null` so the backend stores null and the runtime applies
 * defaults.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, FormControl, FormLabel, FormControlLabel,
  RadioGroup, Radio, InputLabel, Select, MenuItem, TextField,
  Button, Divider, Switch, Tooltip, Collapse, IconButton,
} from '@mui/material';
import {
  KeyboardArrowRight as ChevronIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';

const SCRIPT_TYPES = [
  { value: 'login', label: 'Login' },
  { value: 'progress', label: 'Progress Collection' },
  { value: 'roster', label: 'Roster' },
  { value: 'de-roster', label: 'De-roster' },
  { value: 'account-creation', label: 'Account Creation' },
  { value: 'custom', label: 'Custom' },
];

const EXEC_MODES = [
  { value: 'frontend', label: 'Frontend', desc: 'Runs in the browser via Chrome extension' },
  { value: 'backend', label: 'Backend', desc: 'Runs on server via Playwright (Watch Mode)' },
];

// ---------- Fire Policy helpers ----------

/** Defaults applied at runtime when firePolicy is null. */
const FIRE_POLICY_DEFAULTS = Object.freeze({
  runAt: 'document_idle',
  allFrames: false,
  oncePerTab: false,
  refireOnSpaNav: false,
  world: 'isolated',
  excludePatterns: [],
});

const RUN_AT_OPTIONS = [
  { value: 'document_idle', label: 'Document idle (default)' },
  { value: 'document_end',  label: 'Document end' },
  { value: 'document_start', label: 'Document start' },
];

const WORLD_OPTIONS = [
  { value: 'isolated', label: 'Isolated (default — sandboxed)' },
  { value: 'main',     label: 'Main (page context, framework-aware)' },
];

const TIPS = {
  runAt: 'When in the page lifecycle to fire this script. document_start runs before page JS — needed for monkey-patches. document_end runs at DOM ready. document_idle (default) runs after async settles.',
  allFrames: 'Also fire inside iframes (e.g., embedded LMS frames). Default off.',
  oncePerTab: "Don't re-fire if this script is already running on the same tab. Use for login scripts to avoid double-attempts.",
  refireOnSpaNav: 'Re-fire on history.pushState navigation (no full page load). Off by default — CSS injection survives SPA nav anyway. Required for action-based scripts on SPAs.',
  world: 'Default isolated is safer. Main world needed for React/Vue native value setters that bypass framework state.',
  excludePatterns: "URLs that match the script's main patterns BUT should be skipped. e.g., exclude /login* from a Content Cleanup script that targets the entire domain.",
};

/** Coerce stored value (object/null/undefined) to a fully-populated working draft. */
function readFirePolicy(stored) {
  const src = stored && typeof stored === 'object' ? stored : {};
  return {
    runAt: src.runAt || FIRE_POLICY_DEFAULTS.runAt,
    allFrames: !!src.allFrames,
    oncePerTab: !!src.oncePerTab,
    refireOnSpaNav: !!src.refireOnSpaNav,
    world: src.world || FIRE_POLICY_DEFAULTS.world,
    excludePatterns: Array.isArray(src.excludePatterns)
      ? [...src.excludePatterns]
      : [],
  };
}

/**
 * Strip default values; if everything is default return null so the backend
 * persists null (and the runtime applies all defaults).
 */
function normalizeFirePolicy(draft) {
  if (!draft) return null;
  const out = {};
  if (draft.runAt && draft.runAt !== FIRE_POLICY_DEFAULTS.runAt) out.runAt = draft.runAt;
  if (draft.allFrames) out.allFrames = true;
  if (draft.oncePerTab) out.oncePerTab = true;
  if (draft.refireOnSpaNav) out.refireOnSpaNav = true;
  if (draft.world && draft.world !== FIRE_POLICY_DEFAULTS.world) out.world = draft.world;
  const cleanedPatterns = (draft.excludePatterns || [])
    .map((p) => (typeof p === 'string' ? p.trim() : ''))
    .filter(Boolean);
  if (cleanedPatterns.length > 0) out.excludePatterns = cleanedPatterns;
  return Object.keys(out).length === 0 ? null : out;
}

const FieldLabel = ({ children, tip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
    <Typography variant="caption" sx={{ fontWeight: 600 }}>{children}</Typography>
    {tip && (
      <Tooltip title={tip} arrow placement="top">
        <HelpIcon sx={{ fontSize: 13, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    )}
  </Box>
);

// ---------- Component ----------

const SettingsPanel = ({
  script,
  onSettingsChange,
}) => {
  // Local state for fire-policy toggles so the UI updates immediately.
  // Initial value derives from script.firePolicy; falls back to defaults.
  const initialDraft = useMemo(() => readFirePolicy(script?.firePolicy), [script?.firePolicy]);
  const [fpDraft, setFpDraft] = useState(initialDraft);
  const [fpOpen, setFpOpen] = useState(false);
  // textarea state for excludePatterns (kept as string for natural editing)
  const [excludeText, setExcludeText] = useState(
    () => (initialDraft.excludePatterns || []).join('\n')
  );

  /** Persist the current draft (after a single mutation) up to the parent. */
  const commitDraft = useCallback(
    (nextDraft) => {
      setFpDraft(nextDraft);
      onSettingsChange({ firePolicy: normalizeFirePolicy(nextDraft) });
    },
    [onSettingsChange]
  );

  if (!script) return null;

  const settings = {
    executionMode: script.executionMode || 'frontend',
    scriptType: script.scriptType || script.type || 'login',
    appKey: script.appKey || '',
    chainedScripts: script.chainedScripts || [],
    schedule: script.schedule || null,
  };

  const update = (field, value) => {
    onSettingsChange({ [field]: value });
  };

  const updateFp = (field, value) => {
    commitDraft({ ...fpDraft, [field]: value });
  };

  const handleExcludeBlur = () => {
    const lines = excludeText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    commitDraft({ ...fpDraft, excludePatterns: lines });
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Execution Mode */}
      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
          Execution Mode
        </FormLabel>
        <RadioGroup
          row
          value={settings.executionMode}
          onChange={(e) => update('executionMode', e.target.value)}
        >
          {EXEC_MODES.map((m) => (
            <FormControlLabel
              key={m.value}
              value={m.value}
              control={<Radio size="small" />}
              label={m.label}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {/* Script Type */}
      <FormControl size="small" fullWidth sx={{ mb: 2 }}>
        <InputLabel>Script Type</InputLabel>
        <Select
          value={settings.scriptType}
          label="Script Type"
          onChange={(e) => update('scriptType', e.target.value)}
        >
          {SCRIPT_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* App Key */}
      <TextField
        label="App Key"
        size="small"
        fullWidth
        value={settings.appKey}
        onChange={(e) => update('appKey', e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{ readOnly: true }}
        helperText="Set during script creation; changes require admin action"
      />

      <Divider sx={{ my: 2 }} />

      {/* Schedule */}
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Schedule
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {settings.schedule ? (
          <>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {settings.schedule.cron || 'Custom schedule'}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => update('schedule', null)}
            >
              Disable
            </Button>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              Not scheduled
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => update('schedule', { cron: '0 2 * * *', enabled: true })}
            >
              Enable Schedule
            </Button>
          </>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ───────── Fire Policy (collapsible, optional) ───────── */}
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setFpOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setFpOpen((o) => !o);
          }
        }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
          py: 0.5, userSelect: 'none',
          '&:hover': { '& .fp-title': { color: 'primary.main' } },
        }}
        data-testid="fire-policy-toggle"
      >
        <IconButton
          size="small"
          sx={{
            transform: fpOpen ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s',
            p: 0.25,
          }}
          tabIndex={-1}
          aria-label={fpOpen ? 'Collapse fire policy' : 'Expand fire policy'}
        >
          <ChevronIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          className="fp-title"
          sx={{ transition: 'color 0.15s' }}
        >
          Fire Policy
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          (advanced — all fields optional)
        </Typography>
      </Box>

      <Collapse in={fpOpen} timeout="auto" unmountOnExit>
        <Box sx={{ pt: 1, pl: 1, display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="fire-policy-section">
          {/* Run At */}
          <Box>
            <FieldLabel tip={TIPS.runAt}>Run At</FieldLabel>
            <FormControl size="small" fullWidth>
              <Select
                value={fpDraft.runAt}
                onChange={(e) => updateFp('runAt', e.target.value)}
                inputProps={{ 'data-testid': 'fp-runAt' }}
              >
                {RUN_AT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* World */}
          <Box>
            <FieldLabel tip={TIPS.world}>World</FieldLabel>
            <FormControl size="small" fullWidth>
              <Select
                value={fpDraft.world}
                onChange={(e) => updateFp('world', e.target.value)}
                inputProps={{ 'data-testid': 'fp-world' }}
              >
                {WORLD_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Switches */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Tooltip title={TIPS.allFrames} arrow placement="left">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={fpDraft.allFrames}
                    onChange={(e) => updateFp('allFrames', e.target.checked)}
                    inputProps={{ 'data-testid': 'fp-allFrames' }}
                  />
                }
                label={<Typography variant="body2">Run in all frames</Typography>}
              />
            </Tooltip>

            <Tooltip title={TIPS.oncePerTab} arrow placement="left">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={fpDraft.oncePerTab}
                    onChange={(e) => updateFp('oncePerTab', e.target.checked)}
                    inputProps={{ 'data-testid': 'fp-oncePerTab' }}
                  />
                }
                label={<Typography variant="body2">Once per tab</Typography>}
              />
            </Tooltip>

            <Tooltip title={TIPS.refireOnSpaNav} arrow placement="left">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={fpDraft.refireOnSpaNav}
                    onChange={(e) => updateFp('refireOnSpaNav', e.target.checked)}
                    inputProps={{ 'data-testid': 'fp-refireOnSpaNav' }}
                  />
                }
                label={<Typography variant="body2">Re-fire on SPA navigation</Typography>}
              />
            </Tooltip>
          </Box>

          {/* Exclude URL patterns */}
          <Box>
            <FieldLabel tip={TIPS.excludePatterns}>Exclude URL patterns</FieldLabel>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              placeholder={'https://*.example.com/login*\nhttps://example.com/admin/*'}
              value={excludeText}
              onChange={(e) => setExcludeText(e.target.value)}
              onBlur={handleExcludeBlur}
              helperText="One pattern per line. Saved on blur."
              inputProps={{ 'data-testid': 'fp-excludePatterns' }}
            />
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Leave fields at defaults to apply runtime defaults
            (document_idle, isolated world, no exclusions).
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

export default SettingsPanel;
